/*
 * Copyright 2025 Intuition Robotics ltd
 */
/*
 * A typescript & react boilerplate with api call example
 *
 * Copyright (C) 2018  Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Module} from "@intuitionrobotics/ts-common";
import {QueryParam_Email, QueryParam_JWT, QueryParam_RedirectUrl} from "@intuitionrobotics/user-account/shared/api";
import {AccountModule, LoggedStatus, OnLoginStatusUpdated} from "@intuitionrobotics/user-account/frontend";
import {PushPubSubModule} from "@intuitionrobotics/push-pub-sub/frontend";

type Config = {
    envName: string
    kasperoUrl: string
}

export class EnvironmentModule_Class
    extends Module<Config>
    implements OnLoginStatusUpdated {

    constructor() {
        super("EnvironmentModule");
    }

    onLoginStatusUpdated = () => {
        if (AccountModule.getLoggedStatus() === LoggedStatus.LOGGED_IN)
            this.onLogin();
    };

    getOrigin = () => {
        const jwtParam = `${QueryParam_JWT}=${QueryParam_JWT.toUpperCase()}`;
        const userEmailParam = `${QueryParam_Email}=${QueryParam_Email.toUpperCase()}`;
        return `${this.getOriginUrl()}${this.getPathname()}?${jwtParam}&${userEmailParam}&${this.getSearch()}`;
    };

    getPathname() {
        return window.location.pathname;
    }

    getKasperoUrl() {
        return this.config.kasperoUrl;
    }

    getOriginUrl() {
        return window.location.origin;
    }

    getSearch() {
        const search = window.location.search;
        return search.startsWith('?') ? search.slice(1) : search;
    }

    getQuery() {
        return window.location.pathname + window.location.search;
    }

    getEnvName() {
        return this.config.envName.toUpperCase();
    }

    isProd() {
        return this.getEnvName() === 'PROD';
    }

    getAppVersion() {
        return process.env.appVersion;
    }

    getLoginRedirectUrl = (logout?: boolean): string => {
        const encodedOrigin = encodeURIComponent(this.getOrigin());
        return `${this.getKasperoUrl()}${logout ? '/logout' : ''}?${QueryParam_RedirectUrl}=${encodedOrigin}`;
    };

    redirectToLogin() {
        window.location.href = this.getLoginRedirectUrl();
    }

    logout = () => {
        AccountModule.logout(this.getLoginRedirectUrl(true));
    };

    private onLogin = () => {
        PushPubSubModule.initApp();
    };
}

export const EnvironmentModule = new EnvironmentModule_Class();
